import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Trash2, Search, ShieldAlert, CheckCircle2, XCircle, Ban, Clock,
  ArrowLeft, Loader2, AlertTriangle, X,
} from "lucide-react";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import TableToolbar from "@/components/TableToolbar";

const VIEWS = [
  { key: "pending",   label: "Pending" },
  { key: "approved",  label: "Approved" },
  { key: "completed", label: "Completed" },
  { key: "rejected",  label: "Rejected" },
  { key: "cancelled", label: "Cancelled" },
  { key: "all",       label: "All" },
];

const STATUS_META = {
  pending:   { bg: "#F7900918", fg: "#F79009", Icon: Clock,         label: "Pending" },
  approved:  { bg: "#12B76A18", fg: "#12B76A", Icon: CheckCircle2,  label: "Approved" },
  completed: { bg: "#1d355718", fg: "#1d3557", Icon: CheckCircle2,  label: "Completed" },
  rejected:  { bg: "#e6394618", fg: "#e63946", Icon: XCircle,       label: "Rejected" },
  cancelled: { bg: "#66708518", fg: "#667085", Icon: Ban,           label: "Cancelled" },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const { Icon } = meta;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ background: meta.bg, color: meta.fg }}
      data-testid={`ad-status-${status}`}
    >
      <Icon size={10} /> {meta.label}
    </span>
  );
};

// ------------------------ Main list ------------------------
export default function AdminDeletionRequests() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [view, setView] = useState("pending");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // request doc opened in the drawer

  const load = () => {
    setLoading(true);
    const params = {};
    if (view !== "all") params.status = view;
    if (q) params.q = q;
    api.get("/admin/deletion-requests", { params })
      .then((r) => { setRows(r.data.items || []); setCounts(r.data.counts || {}); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load,   [view]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
     
  }, [q]);

  const onActioned = (updated) => {
    setActive(updated);
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-deletion-requests">
      <header>
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946]">Compliance</div>
        <h1 className="text-[26px] font-bold text-[#1d3557] leading-tight flex items-center gap-2">
          <Trash2 size={18} className="text-[#457b9d]" />
          Account Deletion Requests
        </h1>
        <p className="text-[13px] text-[#667085] mt-1">
          Review and process user requests to delete their Fender account per the{" "}
          <a href="/account-deletion" className="text-[#457b9d] hover:text-[#e63946] font-semibold underline">Account Deletion Policy</a>.
        </p>
      </header>

      <div className="bg-white rounded-xl border border-[#E4E7EC] overflow-hidden">
        <div className="flex items-center gap-1 border-b border-[#E4E7EC] px-2 py-2 overflow-x-auto" data-testid="ad-views">
          {VIEWS.map((v) => {
            const n = counts[v.key];
            const isActive = view === v.key;
            return (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                data-testid={`ad-view-${v.key}`}
                className={`h-8 px-3 rounded-md text-[12px] font-semibold whitespace-nowrap ${
                  isActive
                    ? "bg-[#1d3557] text-white"
                    : "text-[#667085] hover:bg-[#F1FAEE] hover:text-[#1d3557]"
                }`}
              >
                {v.label}
                {typeof n === "number" && v.key !== "all" && (
                  <span className={`ml-1.5 text-[10px] font-mono ${isActive ? "text-white/70" : "text-[#a8dadc]"}`}>{n}</span>
                )}
              </button>
            );
          })}
        </div>

        <TableToolbar>
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#a8dadc]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by ticket, name, email, workshop…"
              data-testid="ad-search"
              className="w-full h-9 pl-8 pr-3 rounded-md border border-[#E4E7EC] text-[13px] focus:border-[#457b9d] outline-none"
            />
          </div>
        </TableToolbar>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="ad-table">
            <thead className="bg-[#F7F9FC] text-[#667085] text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Ticket</th>
                <th className="text-left px-3 py-2 font-semibold">Requester</th>
                <th className="text-left px-3 py-2 font-semibold">Workshop</th>
                <th className="text-left px-3 py-2 font-semibold">Role</th>
                <th className="text-left px-3 py-2 font-semibold">Submitted</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-center py-10 text-[#667085]">
                  <Loader2 size={14} className="inline mr-1.5 animate-spin" /> Loading…
                </td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-14 text-[#667085]">
                  <Trash2 size={36} className="mx-auto text-[#a8dadc] mb-2" />
                  <div className="text-[14px] font-semibold text-[#1d3557]">Nothing to review</div>
                  <div className="text-[12px]">No deletion requests match this filter.</div>
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setActive(r)}
                  className="border-t border-[#F1FAEE] hover:bg-[#F7F9FC] cursor-pointer"
                  data-testid={`ad-row-${r.ticket_reference}`}
                >
                  <td className="px-3 py-2.5 font-mono font-semibold text-[#457b9d]">
                    {r.ticket_reference}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-[#1d3557] notranslate">{r.requester_name || "—"}</div>
                    <div className="text-[11.5px] text-[#667085] notranslate">{r.requester_email}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="text-[#1d3557] notranslate">{r.workshop_name || "—"}</div>
                    {r.is_owner && (
                      <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider bg-[#e63946]/12 text-[#e63946]">
                        <ShieldAlert size={9} /> Owner · cascades
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-[12px] text-[#667085] capitalize">{(r.requester_role || "").replace("_", " ") || "—"}</td>
                  <td className="px-3 py-2.5 text-[12px] text-[#667085]">{r.requested_at ? fmtDateTime(r.requested_at) : "—"}</td>
                  <td className="px-3 py-2.5"><StatusPill status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {active && (
        <DetailDrawer
          req={active}
          onClose={() => setActive(null)}
          onActioned={onActioned}
        />
      )}
    </div>
  );
}

// ------------------------ Detail drawer ------------------------
function DetailDrawer({ req, onClose, onActioned }) {
  const [notes, setNotes] = useState(req.admin_notes || "");
  const [confirmAction, setConfirmAction] = useState(null); // "approved" | "rejected" | "completed"
  const [busy, setBusy] = useState(false);

  useEffect(() => setNotes(req.admin_notes || ""), [req.id]);

  const canApprove  = req.status === "pending";
  const canReject   = ["pending", "approved"].includes(req.status);
  const canComplete = ["pending", "approved"].includes(req.status);
  const isTerminal  = ["rejected", "cancelled", "completed"].includes(req.status);

  const doAction = async (status) => {
    setBusy(true);
    try {
      const r = await api.patch(`/admin/deletion-requests/${req.id}`, {
        status,
        admin_notes: notes.trim(),
      });
      toast.success(`Marked ${status}`);
      onActioned?.(r.data.request);
      setConfirmAction(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="ad-drawer-backdrop">
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[560px] h-full bg-white shadow-2xl overflow-y-auto"
        data-testid="ad-drawer"
      >
        <header className="sticky top-0 bg-white border-b border-[#E4E7EC] px-6 py-4 z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-[#e63946]">
                Deletion Request
              </div>
              <div className="text-[18px] font-bold text-[#1d3557] font-mono">{req.ticket_reference}</div>
              <div className="mt-1"><StatusPill status={req.status} /></div>
            </div>
            <button onClick={onClose} data-testid="ad-drawer-close"
                    className="p-1.5 rounded-md hover:bg-[#F1FAEE] text-[#667085]">
              <X size={16} />
            </button>
          </div>
        </header>

        <div className="px-6 py-5 space-y-5 text-[13px] text-[#1d3557]">
          {req.is_owner && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/8 p-3 flex items-start gap-2" data-testid="ad-drawer-owner-warning">
              <ShieldAlert size={16} className="text-[#e63946] shrink-0 mt-0.5" />
              <div className="text-[12.5px] leading-relaxed">
                <b className="text-[#e63946]">Owner request · cascades to workshop.</b><br />
                Marking this <b>completed</b> will soft-delete every user in <span className="notranslate font-semibold">{req.workshop_name || "the workshop"}</span> and close the garage. Verify with the requester before proceeding.
              </div>
            </div>
          )}

          <section>
            <SectionTitle>Requester</SectionTitle>
            <KV k="Name" v={<span className="notranslate">{req.requester_name || "—"}</span>} />
            <KV k="Email" v={<a href={`mailto:${req.requester_email}`} className="text-[#457b9d] hover:text-[#e63946] font-semibold notranslate">{req.requester_email}</a>} />
            <KV k="Mobile" v={<span className="notranslate">{req.requester_phone || "—"}</span>} />
            <KV k="Role" v={<span className="capitalize">{(req.requester_role || "").replace("_", " ") || "—"}</span>} />
            <KV k="Workshop" v={<span className="notranslate">{req.workshop_name || "—"}</span>} />
            <KV k="User ID" v={<span className="font-mono text-[11.5px] text-[#667085]">{req.user_id}</span>} />
            <KV k="Garage ID" v={<span className="font-mono text-[11.5px] text-[#667085]">{req.garage_id || "—"}</span>} />
          </section>

          <section>
            <SectionTitle>Timeline</SectionTitle>
            <KV k="Submitted" v={req.requested_at ? fmtDateTime(req.requested_at) : "—"} />
            {req.reviewed_at && <KV k="Last actioned" v={fmtDateTime(req.reviewed_at)} />}
            {req.completed_at && <KV k="Completed" v={fmtDateTime(req.completed_at)} />}
          </section>

          {req.reason && (
            <section>
              <SectionTitle>Reason from requester</SectionTitle>
              <div className="p-3 rounded-md bg-[#F7F9FC] border border-[#E4E7EC] whitespace-pre-wrap text-[12.5px]" data-testid="ad-drawer-reason">
                {req.reason}
              </div>
            </section>
          )}

          {req.completion_impact && (
            <section>
              <SectionTitle>Completion impact</SectionTitle>
              <div className="p-3 rounded-md bg-[#F1FAEE] border border-[#a8dadc]/40 text-[12.5px]">
                {req.completion_impact.workshop_closed ? (
                  <>Closed workshop <b>{req.completion_impact.workshop_id?.slice(0,8)}</b> and disabled <b>{req.completion_impact.users_disabled}</b> user(s).</>
                ) : (
                  <>Disabled <b>{req.completion_impact.users_disabled || 1}</b> user account.</>
                )}
              </div>
            </section>
          )}

          <section>
            <SectionTitle>Admin notes</SectionTitle>
            {isTerminal ? (
              <div className="p-3 rounded-md bg-[#F7F9FC] border border-[#E4E7EC] whitespace-pre-wrap text-[12.5px] min-h-[60px]">
                {req.admin_notes || <span className="text-[#a8dadc] italic">No notes recorded.</span>}
              </div>
            ) : (
              <textarea
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={2000}
                placeholder="Add a note the requester will see in the notification email…"
                data-testid="ad-drawer-notes"
                className="w-full rounded-md border border-[#E4E7EC] p-2.5 text-[12.5px] focus:border-[#457b9d] outline-none resize-none"
              />
            )}
            {!isTerminal && (
              <div className="text-[10.5px] text-[#a8dadc] mt-1 text-right">{notes.length}/2000</div>
            )}
          </section>

          {!isTerminal && (
            <section className="pt-2 border-t border-[#F1FAEE] space-y-2">
              <SectionTitle>Actions</SectionTitle>
              <div className="flex flex-wrap gap-2">
                {canApprove && (
                  <button
                    type="button" disabled={busy}
                    onClick={() => setConfirmAction("approved")}
                    data-testid="ad-drawer-approve"
                    className="h-10 px-4 rounded-md bg-[#12B76A] hover:bg-[#0F9256] text-white text-[12.5px] font-semibold disabled:opacity-40 inline-flex items-center gap-1.5"
                  >
                    <CheckCircle2 size={13} /> Approve
                  </button>
                )}
                {canComplete && (
                  <button
                    type="button" disabled={busy}
                    onClick={() => setConfirmAction("completed")}
                    data-testid="ad-drawer-complete"
                    className="h-10 px-4 rounded-md bg-[#1d3557] hover:bg-[#152744] text-white text-[12.5px] font-semibold disabled:opacity-40 inline-flex items-center gap-1.5"
                  >
                    <Trash2 size={13} /> Mark completed
                  </button>
                )}
                {canReject && (
                  <button
                    type="button" disabled={busy}
                    onClick={() => setConfirmAction("rejected")}
                    data-testid="ad-drawer-reject"
                    className="h-10 px-4 rounded-md bg-white border border-[#E4E7EC] text-[12.5px] font-semibold text-[#e63946] hover:bg-[#e63946]/8 disabled:opacity-40 inline-flex items-center gap-1.5"
                  >
                    <XCircle size={13} /> Reject
                  </button>
                )}
              </div>
              <div className="text-[11px] text-[#667085] leading-relaxed">
                <b>Approve</b> = intent recorded, no data change yet.{" "}
                <b>Mark completed</b> = executes soft-delete (and cascades if owner).{" "}
                <b>Reject</b> = closes the request, account stays active.
              </div>
            </section>
          )}
        </div>
      </div>

      {confirmAction && (
        <ConfirmModal
          action={confirmAction}
          req={req}
          notes={notes}
          busy={busy}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => doAction(confirmAction)}
        />
      )}
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <div className="text-[10.5px] uppercase tracking-wider font-bold text-[#667085] mb-2">{children}</div>
);
const KV = ({ k, v }) => (
  <div className="flex items-start justify-between gap-3 py-1 text-[12.5px]">
    <div className="text-[#667085] shrink-0">{k}</div>
    <div className="text-right text-[#1d3557] max-w-[70%] break-words">{v}</div>
  </div>
);

function ConfirmModal({ action, req, notes, busy, onCancel, onConfirm }) {
  const meta = useMemo(() => {
    const m = STATUS_META[action] || STATUS_META.pending;
    const isCascade = action === "completed" && req.is_owner;
    return {
      ...m,
      title: isCascade ? "Complete deletion + close workshop?"
        : action === "completed" ? "Mark this request completed?"
        : action === "approved"  ? "Approve this deletion request?"
        :                          "Reject this deletion request?",
      body: isCascade
        ? `This will soft-delete the owner AND every user in "${req.workshop_name || "the workshop"}" and mark the garage closed. Operational data is retained per policy but the workshop stops appearing to users immediately.`
        : action === "completed"
          ? "This will soft-delete the requester's account. Operational data is retained per policy."
          : action === "approved"
            ? "This records SparesHub's intent to proceed. No data is deleted until you mark it completed."
            : "The account stays active. The requester will be emailed with your admin notes.",
      cta: action === "completed" ? "Complete deletion"
         : action === "approved"  ? "Approve"
         :                          "Reject",
    };
  }, [action, req.is_owner, req.workshop_name]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
         onClick={busy ? undefined : onCancel} data-testid="ad-confirm-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
           data-testid="ad-confirm-dialog">
        <div className="px-6 py-4 border-b border-[#E4E7EC]">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={16} className="text-[#e63946]" />
            <h3 className="text-[15px] font-bold text-[#1d3557]">{meta.title}</h3>
          </div>
          <p className="text-[12.5px] text-[#667085]">{meta.body}</p>
          {!notes.trim() && action !== "approved" && (
            <div className="mt-3 rounded-md bg-[#FEF0C7] border border-[#F79009]/30 px-2.5 py-2 text-[11.5px] text-[#B54708]">
              You haven&apos;t added an admin note. The requester will get a bare status email.
            </div>
          )}
        </div>
        <div className="px-6 py-3 bg-[#F7F9FC] flex justify-end gap-2">
          <button type="button" disabled={busy}
                  onClick={onCancel} data-testid="ad-confirm-cancel"
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white disabled:opacity-50">
            <ArrowLeft size={12} className="inline mr-1" /> Back
          </button>
          <button type="button" disabled={busy}
                  onClick={onConfirm} data-testid="ad-confirm-go"
                  className="h-10 px-4 rounded-md text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
                  style={{ background: meta.fg }}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <meta.Icon size={13} />} {busy ? "Working…" : meta.cta}
          </button>
        </div>
      </div>
    </div>
  );
}
